<template>
  <template v-for="(node, i) in richTextContent">
    <template v-if="isTextNode(node)">
      <strong v-if="node.format === 1" :key="`bold_${i}`" v-text="node.text" />
      <em
        v-else-if="node.format === 2"
        :key="`italic_${i}`"
        v-text="node.text"
      />
      <template v-else>{{ node.text }}</template>
    </template>
    <br v-else-if="isLinebreakNode(node)" :key="`linebreak_${i}`" />
    <template v-else-if="isLinkNode(node)">
      <NuxtLink
        v-if="node.fields.linkType === 'custom'"
        :key="`custom_${i}`"
        :to="node.fields.url"
        :target="node.fields.linkType === 'custom' ? '_blank' : null"
      >
        <PayloadRichText :nested-content="node.children" />
      </NuxtLink>
      <NuxtLink
        v-else-if="node.fields.linkType === 'internal'"
        :key="`internal_${i}`"
        :to="getInternalUrl(node.fields.doc)"
      >
        <PayloadRichText :nested-content="node.children" />
      </NuxtLink>
    </template>
    <template v-else-if="isUploadNode(node)">
      <figure v-if="node.type === 'upload'" :key="i">
        <PayloadImage :image="node.value" :sizes="props.imageSizes" />
        <figcaption v-if="node.fields?.caption">
          <p v-text="node.fields.caption" />
        </figcaption>
      </figure>
    </template>
    <template v-else-if="isHorizontalRuleNode(node)">
      <hr :key="`hr_${i}`" />
    </template>
    <template v-else-if="isBlockNode(node)">
      <Component
        :is="RichTextBlockComponents[node.fields.blockType]"
        v-if="RichTextBlockComponents[node.fields.blockType]"
        :key="`block_${i}`"
        :block="node.fields"
      />
    </template>
    <template v-else-if="isElementNode(node)">
      <Component :is="getElementTag(node)" v-if="getElementTag(node)" :key="i">
        <PayloadRichText :nested-content="node.children" />
      </Component>
      <PayloadRichText
        v-else
        :key="`unwrapped_${i}`"
        :nested-content="node.children"
      />
    </template>
  </template>
</template>

<script setup lang="ts">
import type {
  SerializedLexicalNode,
  SerializedElementNode,
  SerializedTextNode,
} from 'lexical'
import type { DownloadWidgetBlock, Image, Page } from '#payload-types'
import type { LexicalRichTextField } from './types'
import type { SrcsetSizes } from '@/components/payload/image/types'

interface LexicalTextNode extends SerializedTextNode {
  type: 'text'
}

interface LexicalParagraphNode extends SerializedElementNode<LexicalTextNode> {
  type: 'paragraph'
}

interface LexicalHeadingNode extends SerializedElementNode<LexicalTextNode> {
  tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  type: 'heading'
}

interface LexicalListItemNode extends SerializedElementNode<LexicalTextNode> {
  type: 'listitem'
}

interface LexicalListNode extends SerializedElementNode<LexicalListItemNode> {
  tag: 'ol' | 'ul'
  type: 'list'
}

interface LexicalBlockquoteNode extends SerializedElementNode<LexicalTextNode> {
  type: 'quote'
}

type LexicalElementNode =
  | LexicalParagraphNode
  | LexicalHeadingNode
  | LexicalListItemNode
  | LexicalListNode
  | LexicalBlockquoteNode

interface LexicalCustomLinkNode extends SerializedElementNode {
  type: 'link' | 'autolink'
  fields: {
    url: string
    newTab: boolean
    linkType: 'custom'
  }
}

interface LexicalInternalLinkNode extends SerializedElementNode {
  type: 'link' | 'autolink'
  fields: {
    url: string
    doc: {
      relationTo: 'pages'
      value: Page
    }
    newTab: boolean
    linkType: 'internal'
  }
}

type LexicalLinkNode = LexicalCustomLinkNode | LexicalInternalLinkNode

interface LexicalHorizontalRuleNode extends SerializedLexicalNode {
  type: 'horizontalrule'
}

interface LexicalLinebreakNode extends SerializedLexicalNode {
  type: 'linebreak'
}

interface LexicalUploadNode extends SerializedLexicalNode {
  fields?: {
    [key: string]: any
  }
  relationTo: 'images'
  type: 'upload'
  value: Image
}

type LexicalBlockFields = DownloadWidgetBlock

interface LexicalBlockNode extends SerializedLexicalNode {
  type: 'block'
  fields: LexicalBlockFields
}

interface PayloadRichTextProps {
  content?: LexicalRichTextField
  nestedContent?: SerializedLexicalNode[]
  imageSizes?: SrcsetSizes
}

const props = defineProps<PayloadRichTextProps>()

/**
 * Typechecks
 */
const isTextNode = (node: any): node is LexicalTextNode => {
  return typeof node === 'object' && node !== null && node.type === 'text'
}

const isElementNode = (node: any): node is LexicalElementNode => {
  return (
    typeof node === 'object' && node !== null && Array.isArray(node.children)
  )
}

const isLinkNode = (node: any): node is LexicalLinkNode => {
  return (
    typeof node === 'object' &&
    node !== null &&
    Array.isArray(node.children) &&
    ['link', 'autolink'].includes(node.type) &&
    'fields' in node &&
    typeof node.fields === 'object' &&
    ['custom', 'internal'].includes(node.fields.linkType)
  )
}

const isLinebreakNode = (node: any): node is LexicalLinebreakNode => {
  return typeof node === 'object' && node !== null && node.type === 'linebreak'
}

const isHorizontalRuleNode = (node: any): node is LexicalHorizontalRuleNode => {
  return (
    typeof node === 'object' && node !== null && node.type === 'horizontalrule'
  )
}

const isUploadNode = (node: any): node is LexicalUploadNode => {
  return (
    typeof node === 'object' &&
    node !== null &&
    node.type === 'upload' &&
    'relationTo' in node &&
    'value' in node
  )
}

const isBlockNode = (node: any): node is LexicalBlockNode => {
  return (
    typeof node === 'object' &&
    node !== null &&
    node.type === 'block' &&
    typeof node.fields === 'object' &&
    node.fields !== null &&
    'blockType' in node.fields
  )
}

/**
 * Blocks Payload's editor can produce but this renderer doesn't know are
 * skipped: `<Component :is="undefined">` throws during SSR and 500s the whole
 * page.
 */
const RichTextBlockComponents = {
  downloadWidgetBlock: resolveComponent('RichTextDownloadWidget'),
} as const

/**
 * Returns undefined for node types Payload's editor can produce but this
 * renderer doesn't know. The template falls back to rendering the children
 * unwrapped: `<Component :is="undefined">` throws during SSR (500s the whole
 * page) and silently drops the content on the client.
 */
const getElementTag = (node: LexicalElementNode) => {
  switch (node.type) {
    case 'paragraph':
      return 'p'
    case 'quote':
      return 'blockquote'
    case 'listitem':
      return 'li'
    default:
      return node.tag
  }
}

const richTextContent = computed(() => {
  if (props.nestedContent) {
    return props.nestedContent
  }

  if (props.content) {
    return props.content.root.children
  }

  return []
})

const getInternalUrl = (doc: LexicalInternalLinkNode['fields']['doc']) => {
  switch (doc.relationTo) {
    case 'pages':
      return doc.value.route || '/'
    default:
      return '/'
  }
}
</script>
